<template>
  <div :class="'app-order-pps-view' + (lefted ? ' lefted' : '')">
    <template v-if="innerPlainPPs.length > 0">
      <div v-for="pp in innerPlainPPs" :key="pp.id" class="app-order-pps-view-item primary">
        <b>{{ pp.name }}</b>
      </div>
    </template>
    <template v-else-if="isDemo && innerDemoPPs.length > 0">
      <div v-for="pp in innerDemoPPs" :key="pp.id"
        class="app-order-pps-view-item primary">
        <b>{{ pp.name }}</b>
        <i><b-icon icon="star-fill" scale="0.7" class="mar-left" v-b-popover.hover.top="$t('demo-product-package_expl')" /></i>
      </div>
    </template>
    <template v-else>
      <div v-for="group in innerOrderPPGroups" :key="group.key"
      :class="'app-order-pps-view-item ' + (group.orderPP.booked_quantity || group.orderPP.booked_mw || group.orderPP.booked_credits != null ? 'primary' : 'secondary') + (group.packageCount > 1 ? ' grouped' : '')">
        <b>{{ group.name }}</b>
        <i v-if="group.orderPP.booked_mw != null">
          <span v-if="showSubscriptionProgressPerAnalysis && group.orderPP.analysis_mw_within_order_product_package != null">{{ ' (' + formatMw(group.orderPP.analysis_mw_within_order_product_package) + '/' + formatMw(group.orderPP.booked_mw) + ' MW)' }}</span>
          <app-expl-wrap v-else-if="showSubscriptionProgressPerAnalysis" :expl="$t('zombie-analysis_expl')">
            <app-icon icon="zombie" class="mar-left"/>
          </app-expl-wrap>
          <span v-else>{{ ' (' + formatMw(group.orderPP.total_analyses_mw || 0) + '/' + formatMw(group.orderPP.booked_mw) + ' MW)' }}</span>
        </i>
        <i v-else-if="group.orderPP.booked_credits != null">
          <app-expl-wrap v-if="showSubscriptionProgressPerAnalysis && group.orderPP.analysis_credits"
            :expl="creditsExplanation(group.orderPP)">
            {{ ' (' + formatCreditAmount(group.orderPP.analysis_credits.credits) + ')' }}
          </app-expl-wrap>
          <app-expl-wrap v-else-if="showSubscriptionProgressPerAnalysis" :expl="$t('zombie-analysis_expl')">
            <app-icon icon="zombie" class="mar-left"/>
          </app-expl-wrap>
          <span v-else-if="plantCredits != null">{{ ' (' + formatCreditAmount(plantCredits) + ')' }}</span>
          <span v-else>{{ ' (' + formatCredits(group.orderPP.total_analyses_credits || 0, group.orderPP.booked_credits) + ')' }}</span>
        </i>
        <i v-else-if="group.orderPP.booked_quantity">
          <span v-if="showSubscriptionProgressPerAnalysis && group.orderPP.analysis_position_within_order_product_package">{{ ' (' + group.orderPP.analysis_position_within_order_product_package + '/' + group.orderPP.booked_quantity + ')' }}</span>
          <app-expl-wrap v-else-if="showSubscriptionProgressPerAnalysis" :expl="$t('zombie-analysis_expl')">
            <app-icon icon="zombie" class="mar-left"/>
          </app-expl-wrap>
          <span v-else>{{ ' (' + group.orderPP.total_analyses_count + '/' + group.orderPP.booked_quantity + ')' }}</span>
        </i>
        <i v-if="!group.orderPP.booked_quantity && group.orderPP.booked_mw == null && group.orderPP.booked_credits == null"> Setup</i>
        <small class="app-order-pps-view-item-badge" v-if="group.disabledKeyFiguresCount"> -{{ group.disabledKeyFiguresCount }}</small>
      </div>
    </template>
    <div v-if="lefted" class="clear"></div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Prop, Component, Watch } from "vue-property-decorator";
import { OrderProductPackageSchema } from "../../services/volateq-api/api-schemas/order-schema";
import { ProductPackageSchema } from "../../services/volateq-api/api-schemas/product-package";
import { sortBy } from "@/app/shared/services/helper/sort-helper";
import { MathHelper } from "@/app/shared/services/helper/math-helper";
import { OrderHelper } from "@/app/shared/services/helper/order-helper";
import type { OrderPPDisplayGroup } from "@/app/shared/services/helper/order-helper";
import { escapeHtml } from "@/app/shared/services/helper/string-helper";
import AppIcon from "../app-icon/app-icon.vue";
import AppExplWrap from "../app-explanation/app-expl-wrap.vue";

// CSP_PTC_BASE, PV_BASE
const BASE_PACKAGE_IDS = [1, 12];

@Component({
  name: "app-order-pps-view",
  components: {
    AppIcon,
    AppExplWrap,
  },
})
export default class AppOrderPpsView extends Vue {
  @Prop({ default: () => [] }) orderProductPackages!: OrderProductPackageSchema[];
  @Prop({ default: () => [] }) demoProductPackages!: ProductPackageSchema[];
  @Prop({ default: false }) isDemo!: boolean;
  @Prop({ default: false }) lefted!: boolean;
  @Prop({ default: false }) showSubscriptionProgressPerAnalysis!: boolean;
  /** Credits the analyses of one plant took from its Wallet, shown instead of the Wallet's used/booked credits. */
  @Prop({ default: null }) plantCredits!: number | null;
  /** Plain package pills without order progress, e.g. for the packages of an order in the header. */
  @Prop({ default: () => [] }) plainProductPackages!: Pick<ProductPackageSchema, "id" | "name">[];

  innerOrderPPGroups: OrderPPDisplayGroup[] = [];
  innerDemoPPs: ProductPackageSchema[] = [];

  formatMw(num: number): string {
    return MathHelper.formatMw(num);
  }

  formatCreditAmount(credits: number): string {
    return OrderHelper.formatCreditAmount(credits);
  }

  formatCredits(used: number, booked: number): string {
    return OrderHelper.formatCredits(used, booked);
  }

  /** app-expl-wrap renders HTML, so the lines are escaped. */
  creditsExplanation(orderPP: OrderProductPackageSchema): string {
    if (!orderPP.analysis_credits) {
      return "";
    }
    return OrderHelper.creditsExplanationLines(orderPP.analysis_credits).map(escapeHtml).join("<br>");
  }

  created() {
    this.onOrderPPsChanged();
    this.onDemoPPsChanged();
  }

  @Watch('orderProductPackages') onOrderPPsChanged() {
    const orderPPs = sortBy(this.orderProductPackages.filter(orderPP => !BASE_PACKAGE_IDS.includes(orderPP.product_package.id)), opp => opp.product_package.name) // Filter CSP_PTC and PV Base
    this.innerOrderPPGroups = OrderHelper.groupForDisplay(orderPPs);
  }

  get innerPlainPPs(): Pick<ProductPackageSchema, "id" | "name">[] {
    return this.plainProductPackages.filter(pp => !BASE_PACKAGE_IDS.includes(pp.id));
  }

  @Watch('demoProductPackages') onDemoPPsChanged() {
    if (this.demoProductPackages) {
      this.innerDemoPPs = sortBy(this.demoProductPackages.filter(pp => !BASE_PACKAGE_IDS.includes(pp.id)), pp => pp.name);
    }
  }
}
</script>

<style lang="scss">
@import "@/scss/_colors.scss";

.app-order-pps-view {
  &.lefted {
    .app-order-pps-view-item {
      float: left;
      margin-right: 0.5em;
    }
  }

  &-item {
    position: relative;
    font-size: 12px;
    padding: 0.1em 0.75em;
    border-radius: 10em;
    border: 1px solid var(--primary-bg-highlight-color);
    margin-bottom: 0.5em;
    width: 165px;
    text-align: center;

    &:last-child {
      margin-bottom: 0;
    }

    &.primary {
      color: var(--primary-text-color);
      background: var(--primary-bg-highlight-color);
      width: 165px;
    }
    &.secondary {
      background: var(--secondary-bg-color);
    }

    // The packages of a Wallet share one pill, which grows with their names, but not to the full width
    &.grouped {
      width: fit-content;
      min-width: 165px;
      max-width: 100%;
    }

    &-badge {
      position: absolute;
      border-radius: 10px;
      padding: 3px;
      right: -5px;
      top: -5px;
      color: var(--gift-text-color);
      background-color: var(--gift-bg-color);
      height: 15px;
      font-size: 10px;
      line-height: 10px;
    }
  }
}
</style>
